import React, { useEffect, useState } from 'react';
import './CustomCursor.css';
import { ENABLE_CUSTOM_CURSOR, CURSOR_SETTINGS } from '../config/cursorConfig';

/**
 * CustomCursor Component
 * 
 * Renders a custom cursor that is visible in Discord/OBS streams.
 * The Windows cursor is not captured by screen recording software,
 * but this React-rendered cursor is.
 * 
 * Features:
 * - Tracks mouse position in real-time
 * - Doesn't interfere with game mechanics (pointer-events: none)
 * - Changes appearance based on hover state (pointer vs default cursor)
 * - Smooth 60fps updates
 * - Can be disabled via cursorConfig.js
 */
const CustomCursor = () => {
  // Don't render if custom cursor is disabled
  if (!ENABLE_CUSTOM_CURSOR) {
    return null;
  }
  const [position, setPosition] = useState({ x: -100, y: -100 });
  const [isPointer, setIsPointer] = useState(false);

  useEffect(() => {
    // Force hide all native cursors by overriding styles
    const transparentCursor = "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAAAtJREFUGFdjYAACAAAFAAGq1chRAAAAAElFTkSuQmCC'), auto";
    
    // Apply transparent cursor to body and html
    document.body.style.setProperty('cursor', transparentCursor, 'important');
    document.documentElement.style.setProperty('cursor', transparentCursor, 'important');
    
    // Override cursor on element (but skip modals and their children)
    const overrideCursor = (element) => {
      if (element && element.style) {
        // Skip modal elements and their children
        // Check if element is a modal or inside a modal
        const isModalElement = element.classList?.contains('modal-overlay') || 
                              element.classList?.contains('modal-content') ||
                              element.classList?.contains('confirmation-overlay') ||
                              element.classList?.contains('confirmation-dialog') ||
                              element.classList?.contains('notification-toast') ||
                              element.closest?.('.modal-overlay') ||
                              element.closest?.('.modal-content') ||
                              element.closest?.('.confirmation-overlay') ||
                              element.closest?.('.confirmation-dialog') ||
                              element.closest?.('.notification-toast');
        
        // Also check if element is an input/textarea/select inside a modal
        const isInputInModal = (element.tagName === 'INPUT' || 
                               element.tagName === 'TEXTAREA' || 
                               element.tagName === 'SELECT') &&
                               (element.closest?.('.modal-overlay') || 
                                element.closest?.('.modal-content') ||
                                element.closest?.('.confirmation-overlay') ||
                                element.closest?.('.confirmation-dialog'));
        
        if (isModalElement || isInputInModal) {
          return; // Don't override cursor for modal elements or inputs in modals
        }
        element.style.setProperty('cursor', transparentCursor, 'important');
      }
    };
    
    // Override all existing elements
    const overrideAllCursors = () => {
      overrideCursor(document.body);
      overrideCursor(document.documentElement);
      const allElements = document.querySelectorAll('*');
      allElements.forEach(overrideCursor);
    };
    
    // Run initially
    overrideAllCursors();
    
    // Watch for new elements being added to DOM
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            overrideCursor(node);
            // Also override all children
            const children = node.querySelectorAll('*');
            children.forEach(overrideCursor);
          }
        });
      });
    });
    
    // Start observing
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: false
    });
    
    // Also run periodically as backup
    const intervalId = setInterval(overrideAllCursors, 1000);
    
    let animationFrameId;
    let lastX = -100;
    let lastY = -100;

    // Update cursor position on mouse move
    const handleMouseMove = (e) => {
      // Use requestAnimationFrame for smooth 60fps updates
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }

      animationFrameId = requestAnimationFrame(() => {
        lastX = e.clientX;
        lastY = e.clientY;
        setPosition({ x: lastX, y: lastY });

        // Check if hovering over a clickable element
        const target = e.target;
        const computedStyle = window.getComputedStyle(target);
        const cursorStyle = computedStyle.cursor;
        
        setIsPointer(
          cursorStyle === 'pointer' || 
          target.tagName === 'BUTTON' || 
          target.tagName === 'A' ||
          target.onclick !== null ||
          target.classList.contains('clickable')
        );
      });
    };

    // Hide cursor when leaving window
    const handleMouseLeave = () => {
      setPosition({ x: -100, y: -100 });
    };

    // Show cursor when entering window
    const handleMouseEnter = (e) => {
      setPosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseleave', handleMouseLeave);
    window.addEventListener('mouseenter', handleMouseEnter);

    return () => {
      observer.disconnect();
      clearInterval(intervalId);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('mouseenter', handleMouseEnter);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);

  return (
    <div
      className={`custom-cursor ${isPointer ? 'custom-cursor-pointer' : ''}`}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
      }}
    />
  );
};

export default CustomCursor;
